import React, { useMemo } from 'react';
import { useServices } from '@/components/ServiceContext';
import { applyDefaultFilterOperators } from '@/utils/filter';
import { ServiceDataGrid } from '@/components/ServiceDataGrid';
import { useLocation } from 'react-router-dom';
import { ExportJob } from '@/API';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition.ts';
import {
  GraphQLQuery,
  SearchableFilterInput,
} from '@lambda-layer/types/filter.ts';
import { GridValidRowModel } from '@mui/x-data-grid';
import { FilterDefinitionGridColDefMapper } from '@lambda-layer/mappers/FilterDefinitionGridColDefMapper';
import { FilterDefinitionGraphQLQueryMapper } from '@lambda-layer/mappers/FilterDefinitionGraphQLQueryMapper';

const filterDefinitionGridColDefMapper = new FilterDefinitionGridColDefMapper();
const filterDefinitionGraphQLQueryMapper =
  new FilterDefinitionGraphQLQueryMapper();

export interface ExportPreviewPageState extends URLSearchParams {
  exportJob: ExportJob;
}

const ExportPreviewPage: React.FC = () => {
  const location = useLocation();

  const state = location.state as ExportPreviewPageState;

  const { boughtListService, houseListService, staffSeedService } =
    useServices();

  const filterFields = useMemo(() => {
    return state.exportJob.filterFields
      ? FilterDefinition.createFilterDefinitions(
          JSON.parse(state.exportJob.filterFields)
        )
      : [];
  }, [state.exportJob.filterFields]);

  /**
   * Defines the columns for the data grid.
   */
  const columns = applyDefaultFilterOperators(
    filterDefinitionGridColDefMapper.map(filterFields)
  );

  /**
   * Defines the quick search fields for the data grid.
   */
  const quickSearchFields = applyDefaultFilterOperators(
    filterDefinitionGridColDefMapper.map(filterFields, {
      customFilter: fd => fd.databaseMetadata?.dataType === 'string',
    })
  ).map(col => col.field);

  const handleQueryUpdated = React.useCallback(
    (query: GraphQLQuery<SearchableFilterInput<GridValidRowModel>>) => {
      const filterSelections = JSON.parse(state.exportJob.filterInput);
      const graphQLQuery = filterDefinitionGraphQLQueryMapper.map({
        filterDefinitions: filterFields,
        filterSelections,
      });
      if (query) {
        // Create or append to an "and" clause
        if (query.and) {
          query.and.push(...graphQLQuery);
        } else {
          query.and = graphQLQuery;
        }
      }
      return query;
    },
    [filterFields, state.exportJob.filterInput]
  );

  return (
    <ServiceDataGrid
      service={(() => {
        if (state.exportJob.listType === 'boughtlist') {
          return boughtListService;
        }
        if (state.exportJob.listType === 'houselist') {
          return houseListService;
        }
        if (state.exportJob.listType === 'staffseed') {
          return staffSeedService;
        }
        throw new Error(`Unsupported list type: ${state.exportJob.listType}`);
      })()}
      columns={columns}
      pageSize={10}
      onQueryUpdated={handleQueryUpdated}
      quickSearchFields={quickSearchFields}
    />
  );
};

export default ExportPreviewPage;
